import { Component, Input, OnChanges } from '@angular/core';
declare var google: any;

/**
 * Multi-series Google chart for the usage-reporting screens.
 * `or-trend-chart` is single-series with no legend, so this is the
 * multi-series equivalent: 2px lines / thin marks, hairline gridlines, a
 * legend for two or more series, and a single all-series tooltip per x
 * (focusTarget 'category'). `type` picks the form: 'line' for trends,
 * 'column' (optionally stacked) for bucketed activity, 'bar' for top-N
 * horizontal comparisons.
 */
@Component({
    selector: 'or-usage-trend-chart',
    templateUrl: './usage-trend-chart.component.html',
    styleUrls: ['./usage-trend-chart.component.css'],
    standalone: false
})
export class UsageTrendChartComponent implements OnChanges {

  static count = 0;

  /** Column headers; the first names the x value (e.g. 'Day'). */
  @Input() columns: string[] = [];
  /** One row per x value: [Date | string, series1, series2, …] matching columns. */
  @Input() rows: (Date | string | number)[][] = [];
  /** One hex per series, from the validated chart palette. */
  @Input() colors: string[] = [];
  @Input() height = 280;
  @Input() type: 'line' | 'column' | 'bar' = 'line';
  /** Stack the series (column charts only). */
  @Input() stacked = false;

  id: string;

  constructor() {
    UsageTrendChartComponent.count++;
    this.id = 'usage_trend_chart_' + UsageTrendChartComponent.count;
  }

  ngOnChanges(): void {
    this.drawChart();
  }

  private drawChart(): void {
    if (this.rows.length === 0 || this.columns.length < 2) return;

    const options = this.buildOptions();
    google.charts.load('current', { packages: ['corechart'] });
    google.charts.setOnLoadCallback(() => {
      const chartData = google.visualization.arrayToDataTable(
        [this.columns as (string | Date | number)[]].concat(this.rows)
      );
      setTimeout(() => {
        const chartDiv = document.getElementById(this.id);
        if (!chartDiv) return;
        const chart = this.type === 'column'
          ? new google.visualization.ColumnChart(chartDiv)
          : this.type === 'bar'
            ? new google.visualization.BarChart(chartDiv)
            : new google.visualization.LineChart(chartDiv);
        chart.draw(chartData, options);
      }, 0);
    });
  }

  private buildOptions(): Record<string, unknown> {
    const seriesCount = this.columns.length - 1;
    const legend = seriesCount > 1
      ? { position: 'bottom', textStyle: { color: '#52514e', fontSize: 12 } }
      : { position: 'none' };
    const valueAxis = {
      gridlines: { color: '#e1e0d9', count: 4 },
      minorGridlines: { color: 'none' },
      baselineColor: '#c3c2b7',
      textStyle: { color: '#898781', fontSize: 11 },
      format: 'short',
      viewWindow: { min: 0 }
    };
    const categoryTextStyle = { color: '#898781', fontSize: 11 };

    if (this.type === 'bar') {
      // Horizontal: the value axis is hAxis, categories on vAxis. Wide left
      // margin so category labels (page routes, control names) stay readable.
      return {
        colors: this.colors,
        backgroundColor: 'transparent',
        legend: legend,
        bar: { groupWidth: '60%' },
        chartArea: { top: 8, right: 16, bottom: 32, left: 180 },
        hAxis: valueAxis,
        vAxis: { textStyle: categoryTextStyle },
        width: '100%',
        height: this.height
      };
    }

    return {
      colors: this.colors,
      lineWidth: 2,
      pointSize: 0,
      focusTarget: 'category',
      backgroundColor: 'transparent',
      isStacked: this.type === 'column' && this.stacked,
      bar: { groupWidth: '70%' },
      legend: legend,
      chartArea: { top: 12, right: 12, bottom: seriesCount > 1 ? 56 : 32, left: 56 },
      vAxis: valueAxis,
      hAxis: {
        format: this.type === 'line' ? 'M/d' : 'h:mm a',
        gridlines: { color: 'none' },
        baselineColor: '#c3c2b7',
        textStyle: categoryTextStyle
      },
      width: '100%',
      height: this.height
    };
  }
}
